import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Input, OnInit, Signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EpcotConfig, QuickBetConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SmoothScroll } from '@frontend/sports/common/core/feature/smooth-scroll';
import { DsButton } from '@frontend/ui/button';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { RewardTokenContext } from '../../reward-tokens/reward-tokens.model';
import { QuickBetActions } from '../quick-bet-actions';
import { quickBetStateSelector } from '../quick-bet.selectors';
import { IQuickBetState } from '../quick-bet.state';
import { QuickBetAutoScrollingService } from '../services/quick-bet-auto-scrolling.service';
import { QuickBetUtils } from '../services/quick-bet-utils.service';
import { QuickBetActionButtonComponent } from './quick-bet-action-button.component';

@Component({
    selector: 'bs-quick-bet-footer',
    imports: [CommonModule, DsButton, QuickBetActionButtonComponent],
    templateUrl: './quick-bet-footer.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./quick-bet-footer.component.scss'],
})
export class QuickBetFooterComponent implements OnInit, AfterViewInit {
    @Input() rewardTokenContext: RewardTokenContext;

    @ViewChild('footer') private footerElement: ElementRef;

    protected quickBetState: Signal<IQuickBetState>;
    protected isEpcot: boolean;

    constructor(
        public quickBetUtils: QuickBetUtils,
        public sitecore: Sitecore,
        public quickBetConfig: QuickBetConfig,
        private store: Store<IBetslipRootState>,
        private smoothScrollService: SmoothScroll,
        private autoScrollingService: QuickBetAutoScrollingService,
        private epcotConfig: EpcotConfig,
        private destroyRef: DestroyRef,
    ) {
        this.isEpcot = this.epcotConfig.isEnabled;
        this.quickBetState = this.store.selectSignal(quickBetStateSelector);
    }

    ngOnInit(): void {
        this.autoScrollingService.shouldAutoScroll$
            .pipe(
                filter((x) => x),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.scrollCtaIntoView());
    }

    addToBetslipClick = () => {
        if (this.quickBetState().inOverlay) {
            this.store.dispatch(QuickBetActions.closeQuickBetUiOverlay({ eventName: undefined }));
        } else {
            this.store.dispatch(QuickBetActions.openBetslip());
        }
    };

    ngAfterViewInit(): void {
        this.scrollCtaIntoView();
    }

    private scrollCtaIntoView(): void {
        if (!this.footerElement) {
            return;
        }

        const nativeFooterElement = this.footerElement.nativeElement;

        if (!nativeFooterElement) {
            return;
        }

        const parentElement = nativeFooterElement.parentElement;

        if (!parentElement) {
            return;
        }

        setTimeout(() => this.scrollToBottom(), 800);
    }

    private scrollToBottom(): void {
        if (!this.footerElement) {
            return;
        }

        const nativeFooterElement = this.footerElement.nativeElement;

        if (!nativeFooterElement) {
            return;
        }

        const parentElement = nativeFooterElement.parentElement;

        if (!parentElement) {
            return;
        }

        const parentRect = parentElement.getBoundingClientRect();
        const footerRect = nativeFooterElement.getBoundingClientRect();
        const delta = footerRect.bottom - parentRect.bottom;

        if (delta === 0) {
            return;
        }

        this.smoothScrollService.scrollTopTo(parentElement, parentElement.scrollTop + delta, 300, true);
    }
}
